import { useMemo, useState } from 'react';
import { useSatellitePreference } from '../hooks/useSatellitePreference';
import { formatNumber } from '../lib/format';
import type { ScorecardRow } from '../types';

type SortField =
  | 'name'
  | 'fbcName'
  | 'totalCalls'
  | 'answerRate'
  | 'qualityRate'
  | 'assessmentsBooked'
  | 'interviewsScheduled';

type Props = {
  rows: ScorecardRow[];
  showFbcColumn: boolean;
  onDrillIn: (afNumber: string) => void;
};

const COLUMNS: Array<{
  key: SortField;
  label: string;
  numeric?: boolean;
  hideWhenNoFbc?: boolean;
}> = [
  { key: 'name', label: 'Franchisee' },
  { key: 'fbcName', label: 'FBC', hideWhenNoFbc: true },
  { key: 'totalCalls', label: 'Calls', numeric: true },
  { key: 'answerRate', label: 'Answer', numeric: true },
  { key: 'qualityRate', label: 'Quality', numeric: true },
  { key: 'assessmentsBooked', label: 'Assess.', numeric: true },
  { key: 'interviewsScheduled', label: 'Interviews', numeric: true },
];

function compare(a: ScorecardRow, b: ScorecardRow, field: SortField): number {
  if (field === 'name') return (a.name ?? '').localeCompare(b.name ?? '');
  if (field === 'fbcName') return (a.fbcName ?? '').localeCompare(b.fbcName ?? '');
  const av = Number((a as unknown as Record<string, unknown>)[field] ?? 0);
  const bv = Number((b as unknown as Record<string, unknown>)[field] ?? 0);
  return av - bv;
}

export default function ScorecardTable({ rows, showFbcColumn, onDrillIn }: Props) {
  const [sortBy, setSortBy] = useState<SortField>('totalCalls');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showSatellites, setShowSatellites] = useSatellitePreference();

  const sharedCountByParent = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) {
      if (r.isSatellite && r.hasOwnOffice === false && r.parentAf) {
        m.set(r.parentAf, (m.get(r.parentAf) ?? 0) + 1);
      }
    }
    return m;
  }, [rows]);

  const filtered = useMemo(() => {
    if (showSatellites) return rows;
    return rows.filter((r) => r.hasOwnOffice !== false);
  }, [rows, showSatellites]);

  const sorted = useMemo(() => {
    const out = filtered.slice().sort((a, b) => compare(a, b, sortBy));
    if (sortOrder === 'desc') out.reverse();
    return out;
  }, [filtered, sortBy, sortOrder]);

  const hiddenCount = rows.length - filtered.length;

  function toggleSort(field: SortField) {
    if (sortBy === field) {
      setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  }

  const visibleColumns = COLUMNS.filter((c) => !(c.hideWhenNoFbc && !showFbcColumn));

  return (
    <div className="ds-panel">
      {(hiddenCount > 0 || showSatellites) && (
        <div className="ds-satellite-toggle-row">
          <label className="ds-satellite-toggle">
            <input
              type="checkbox"
              checked={showSatellites}
              onChange={(e) => setShowSatellites(e.target.checked)}
            />
            <span>
              Show additional territories sharing an office
              {hiddenCount > 0 && !showSatellites ? ` (${hiddenCount} hidden)` : ''}
            </span>
          </label>
        </div>
      )}
      <table className="ds-table">
        <thead>
          <tr>
            <th style={{ width: 28 }}></th>
            {visibleColumns.map((c) => (
              <th
                key={c.key}
                className={`sortable ${c.numeric ? 'num' : ''} ${sortBy === c.key ? 'is-sorted' : ''}`}
                onClick={() => toggleSort(c.key)}
              >
                {c.label}
                {sortBy === c.key && (
                  <span style={{ marginLeft: 4, fontSize: 10 }}>
                    {sortOrder === 'desc' ? '▼' : '▲'}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={visibleColumns.length + 1} style={{ textAlign: 'center', color: '#8a95a5' }}>
                No franchisees in scope
              </td>
            </tr>
          ) : (
            sorted.map((r) => (
              <tr key={r.afNumber} onClick={() => onDrillIn(r.afNumber)}>
                <td>
                  <span className={`ds-marker ${r.status}`} />
                </td>
                <td>
                  <div className="ds-name">
                    {r.name || r.afNumber}
                    {(() => {
                      const satCount = sharedCountByParent.get(r.afNumber) ?? 0;
                      if (satCount === 0 || showSatellites) return null;
                      return (
                        <span
                          className="ds-name-territory-badge"
                          title={`Includes ${satCount} additional territor${satCount === 1 ? 'y' : 'ies'} sharing this office`}
                        >
                          +{satCount} territor{satCount === 1 ? 'y' : 'ies'}
                        </span>
                      );
                    })()}
                  </div>
                  {r.location && <div className="ds-sub">{r.location}</div>}
                </td>
                {showFbcColumn && (
                  <td style={{ fontSize: 12, color: '#5a6a7a' }}>{r.fbcName || '—'}</td>
                )}
                <td className="num">
                  <span className="num-val">{formatNumber(r.totalCalls)}</span>
                </td>
                <td className="num">
                  <span className="num-pct">{r.answerRate}%</span>
                </td>
                <td className="num">
                  <span className="num-pct">{r.qualityRate}%</span>
                </td>
                <td className="num">
                  <span className="num-pct">{r.assessmentsBooked ?? 0}</span>
                </td>
                <td className="num">
                  <span className="num-pct">{r.interviewsScheduled ?? 0}</span>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
